import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEOJsonLd from './SEOJsonLd';

interface GlobalSEOConfig {
  baseUrl: string;
  siteName: string;
  twitterHandle: string;
  defaultImage: string;
  organization: {
    name: string;
    logo: string;
    contactPoint: {
      telephone: string;
      email: string;
    };
    address: {
      streetAddress: string;
      addressLocality: string;
      postalCode: string;
      addressCountry: string;
    };
    sameAs: string[];
  };
}

interface SEOContextType {
  config: GlobalSEOConfig;
  updatePageSEO: (data: PageSEOData) => void;
  currentPageSEO: PageSEOData | null;
}

interface PageSEOData {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  breadcrumbs?: { name: string; url: string }[];
  structuredData?: Record<string, any>;
}

const defaultConfig: GlobalSEOConfig = {
  baseUrl: 'https://www.groupefabre-environnement.fr',
  siteName: 'Groupe Fabre Environnement',
  twitterHandle: '@GroupeFabre',
  defaultImage: 'https://lovable.dev/opengraph-image-p98pqg.png',
  organization: {
    name: 'Groupe Fabre Environnement',
    logo: 'https://www.groupefabre-environnement.fr/logo.png',
    contactPoint: {
      telephone: '+33-565-45-01-23',
      email: 'contact@groupefabre-environnement.fr'
    },
    address: {
      streetAddress: 'Zone Industrielle',
      addressLocality: 'Figeac',
      postalCode: '46100',
      addressCountry: 'FR'
    },
    sameAs: [
      'https://www.linkedin.com/company/groupe-fabre-environnement',
      'https://www.facebook.com/GroupeFabreEnvironnement'
    ]
  }
};

const SEOContext = createContext<SEOContextType | undefined>(undefined);

interface GlobalSEOProviderProps {
  children: React.ReactNode;
}

export const GlobalSEOProvider: React.FC<GlobalSEOProviderProps> = ({ children }) => {
  const [config] = useState<GlobalSEOConfig>(defaultConfig);
  const [currentPageSEO, setCurrentPageSEO] = useState<PageSEOData | null>(null);
  const location = useLocation();

  // Optimisations d'images et accessibilité
  useEffect(() => {
    try {
      const imgs = document.querySelectorAll('img');
      imgs.forEach((img) => {
        if (!img.getAttribute('loading')) img.setAttribute('loading', 'lazy');
        if (!img.getAttribute('decoding')) img.setAttribute('decoding', 'async');
        if (!img.alt || img.alt.trim() === '') {
          const src = img.getAttribute('src') || '';
          const base = src.split('/').pop()?.split('.')[0]?.replace(/[-_]/g, ' ') || 'image';
          img.setAttribute('alt', `Groupe Fabre Environnement ${base}`);
        }
      });
    } catch (e) {
      console.warn('Image enhancement failed', e);
    }
  }, [location.pathname]);

  // Alerte dev si plusieurs H1
  useEffect(() => {
    const h1s = document.querySelectorAll('h1');
    if (h1s.length > 1) {
      console.warn(`[SEO] ${h1s.length} balises H1 détectées sur ${location.pathname}. Il est recommandé d'en avoir une seule.`);
    }
  }, [location.pathname]);

  // Mémoriser la fonction updatePageSEO pour éviter les re-renders inutiles
  const updatePageSEO = useCallback((data: PageSEOData) => {
    setCurrentPageSEO(data);
  }, []);

  // Générer les données structurées globales de l'organisation
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": config.organization.name,
    "url": config.baseUrl,
    "logo": config.organization.logo,
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": config.organization.contactPoint.telephone,
      "email": config.organization.contactPoint.email,
      "contactType": "customer service",
      "availableLanguage": "French"
    },
    "address": {
      "@type": "PostalAddress",
      "streetAddress": config.organization.address.streetAddress,
      "addressLocality": config.organization.address.addressLocality,
      "postalCode": config.organization.address.postalCode,
      "addressCountry": config.organization.address.addressCountry
    },
    "sameAs": config.organization.sameAs,
    "foundingDate": "1999",
    "numberOfEmployees": "50+",
    "industry": "Waste Management and Recycling",
    "slogan": "Votre partenaire pour la valorisation des déchets",
    "description": "Groupe Fabre Environnement est spécialisé dans la gestion, le recyclage et la valorisation des déchets depuis plus de 25 ans en Occitanie, Auvergne et Île-de-France."
  };

  // Générer les breadcrumbs structurés
  const generateBreadcrumbSchema = (breadcrumbs?: { name: string; url: string }[]) => {
    if (!breadcrumbs || breadcrumbs.length === 0) return null;

    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbs.map((crumb, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": crumb.name,
        "item": `${config.baseUrl}${crumb.url}`
      }))
    };
  };

  // Générer les données WebSite pour la recherche interne
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": config.siteName,
    "url": config.baseUrl,
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${config.baseUrl}/recherche?q={search_term_string}`,
      "query-input": "required name=search_term_string"
    },
    "sameAs": config.organization.sameAs
  };

  return (
    <SEOContext.Provider value={{ config, updatePageSEO, currentPageSEO }}>
      <Helmet>
        {/* Métadonnées globales */}
        <meta name="generator" content="React" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta name="author" content={config.organization.name} />
        <meta name="theme-color" content="#22c55e" />

        {/* Open Graph global */}
        <meta property="og:site_name" content={config.siteName} />
        <meta property="og:locale" content="fr_FR" />

        {/* Twitter Card global */}
        <meta name="twitter:site" content={config.twitterHandle} />
        <meta name="twitter:creator" content={config.twitterHandle} />

        {/* Page-level SEO (title, description, canonical) */}
        {currentPageSEO?.title && <title>{currentPageSEO.title}</title>}
        {currentPageSEO?.description && (
          <meta name="description" content={currentPageSEO.description} />
        )}
        {currentPageSEO?.keywords && (
          <meta name="keywords" content={currentPageSEO.keywords} />
        )}
        <link
          rel="canonical"
          href={currentPageSEO?.canonical || `${config.baseUrl}${location.pathname}`}
        />
        {/* hreflang */}
        <link rel="alternate" hrefLang="fr-FR" href={currentPageSEO?.canonical || `${config.baseUrl}${location.pathname}`} />
        <link rel="alternate" hrefLang="x-default" href={currentPageSEO?.canonical || `${config.baseUrl}${location.pathname}`} />

        {/* Open Graph per-page */}
        <meta property="og:title" content={currentPageSEO?.title || config.siteName} />
        <meta property="og:description" content={currentPageSEO?.description || ''} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={currentPageSEO?.canonical || `${config.baseUrl}${location.pathname}`} />
        <meta property="og:image" content={currentPageSEO?.ogImage || config.defaultImage} />

        {/* Twitter Card per-page */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={currentPageSEO?.title || config.siteName} />
        <meta name="twitter:description" content={currentPageSEO?.description || ''} />
        <meta name="twitter:image" content={currentPageSEO?.ogImage || config.defaultImage} />

        {/* Liens de préconnexion pour la performance */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        
        {/* DNS Prefetch pour les ressources externes */}
        <link rel="dns-prefetch" href="//www.google-analytics.com" />
        <link rel="dns-prefetch" href="//fonts.googleapis.com" />

        {/* Données structurées globales */}
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>

        {/* Breadcrumbs si disponibles */}
        {currentPageSEO?.breadcrumbs && (
          <script type="application/ld+json">
            {JSON.stringify(generateBreadcrumbSchema(currentPageSEO.breadcrumbs))}
          </script>
        )}
        
        {/* Données structurées spécifiques à la page */}
        {currentPageSEO?.structuredData && (
          <script type="application/ld+json">
            {JSON.stringify(currentPageSEO.structuredData)}
          </script>
        )}
      </Helmet>
      <SEOJsonLd />
      {children}
    </SEOContext.Provider>
  );
};

export const useSEO = () => {
  const context = useContext(SEOContext);
  if (!context) {
    throw new Error('useSEO must be used within a GlobalSEOProvider');
  }
  return context;
};

export default GlobalSEOProvider;